import React, { FC, useCallback, useState } from "react";
import { flushSync } from "react-dom";
import { Box, BoxProps } from "@chakra-ui/react";
import { useDispatch } from "react-redux";

import { CaseModel, REPORT_STATUS } from "@shared/models";
import { DownloadIcon } from "@shared/components/Icons";
import { generateFormInitialValues } from "@containers/Cases/components/CaseViewContainer/MachineAnalysis/MachineAnalysis.utils";
import { useDownloadCaseForm } from "@shared/hooks";
import { getFullName } from "@shared/utils";
import { MachineAnalysisDownload } from "@containers/Cases/components/CaseViewContainer/MachineAnalysis/MachineAnalysisDownload";
import { actions } from "@containers/Cases/store";

interface DownloadCaseProps extends BoxProps {
  item: CaseModel;
}

const DownloadCase: FC<DownloadCaseProps> = ({ item, onClick, ...restProps }) => {
  const { handleDownloadPaf, isDownloading, targetRef } = useDownloadCaseForm(getFullName(item, ""));
  const [scoreData, setScoreData] = useState<null | ReturnType<typeof generateFormInitialValues>>(null);
  const dispatch = useDispatch();

  const handleDownloadCase = useCallback(
    async (e: React.MouseEvent<HTMLDivElement>) => {
      onClick?.(e);

      dispatch(
        actions.getCaseData.request({
          caseId: Number(item.id),
          cb: (score) => {
            // Rendered before `toPDF()` runs, which only flushes its own `isDownloading` update.
            flushSync(() => setScoreData(generateFormInitialValues(score)));
            handleDownloadPaf();
          },
        }),
      );
    },
    [dispatch, handleDownloadPaf, item.id, onClick],
  );

  if (item.report_status !== REPORT_STATUS.COMPLETED) return null;

  return (
    <Box {...restProps} onClick={handleDownloadCase}>
      <DownloadIcon />
      {isDownloading && scoreData && (
        <Box px={4} py={2.5}>
          <MachineAnalysisDownload targetRef={targetRef} caseObjUpdatedValues={scoreData} />
        </Box>
      )}
    </Box>
  );
};

export default DownloadCase;
